import {DialogState} from 'components/Dialogs';
import {useTranslation} from 'react-i18next';
import React from 'react';
import {RequirementFromDetailsPage} from '../requirementDetailsPage.types';
import {RequirementStatus, User} from 'types';

import {
	GetAllRequirementsDocument,
	useUpdateRequirementMutation,
} from '../../hooks/requirements.generated';
import {mapToRef} from 'helpers';
import {useNotificationBar} from 'hooks/useNotificationBar';
import {useNavigate} from 'react-router-dom';
import {useGetAllVexClustersQuery} from 'features/AdminSection/hooks/vexClusters.generated';
import {useGetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {RequirementStatusType} from '../RequirementDetailsPage';
import {
	SendApprovalDialog,
	SendApprovalDialogChangeTypeMode,
} from './SendApprovalDialog';
import {ApprovalDialog} from './ApprovalDialog';
import {DeclineDialog} from './DeclineDialog';
import {ListOrDetailsRequirement} from 'features/Requirements/requirements.types';
import {
	RequirementChangeType,
	UpdateRequirementInput,
} from 'types/types.generated';
import {
	RequirementUtilsService,
	CommonRequirementInputFields as Input,
} from 'features/Requirements/RequirementUtils.service';
import {useRequirementsVersions} from 'features/Requirements/hooks/useRequirementsVersions';
import {ConfirmWithoutApproval} from './ConfirmWithoutApproval';
import {setCommentRange} from 'typescript';

export interface RegulationFormProps {
	dialogType: RequirementStatusType;
	dialogState: DialogState;
	hideDialog: () => void;
	requirementDetails: RequirementFromDetailsPage;
}

export const WorkFlowDialogHandler = ({
	dialogType,
	dialogState,
	hideDialog,
	requirementDetails,
}: RegulationFormProps) => {
	const {t} = useTranslation('features/requirements', {
		keyPrefix: 'WorkflowDialog',
	});

	const navigate = useNavigate();
	const [users, setUsers] = React.useState<User[]>([]);
	const [dialogChangeType, setDialogChangeType] =
		React.useState<RequirementChangeType>(requirementDetails.changeType);
	const {setMessage} = useNotificationBar();
	const [reason, setReason] = React.useState('');
	const {data: vexData} = useGetAllVexClustersQuery();
	const {data: keywordsData} = useGetAllKeywordsQuery();
	const {getCommonInputFields: mapToInput} = new RequirementUtilsService();

	const [updateRequirementDocument] = useUpdateRequirementMutation();

	const {getInitialVersion, incrementMinorVersion, incrementMajorVersion} =
		useRequirementsVersions();

	const getDeclinedRequirementStatus = (
		requirementDetails: RequirementFromDetailsPage,
	): RequirementStatus => {
		const isNewRequirement =
			!requirementDetails?.changeType ||
			requirementDetails?.changeType === RequirementChangeType.None;

		return isNewRequirement
			? RequirementStatus.Draft
			: RequirementStatus.Modified;
	};

	const deDuplicateUsers = (users: User[]): User[] => {
		const seen = new Set<string>();
		return users.filter(user => {
			if (!user?.id) return false;
			if (seen.has(user.id)) return false;
			seen.add(user.id);
			return true;
		});
	};

	const vexClusters = React.useMemo(
		() => vexData?.vexClusters ?? [],
		[vexData],
	);
	const keywords = React.useMemo(
		() => keywordsData?.keywords ?? [],
		[keywordsData],
	);

	const prePopulatedApprovers = React.useMemo((): User[] => {
		const vexClusterRefs = requirementDetails.vexClusters;
		const clusterApprovers = vexClusterRefs?.flatMap(e => {
			const matchedVexCluster = vexClusters?.find(f => [e.id].includes(f.id));
			return matchedVexCluster?.approvers ?? [];
		});
		if (clusterApprovers?.length >= 1)
			return deDuplicateUsers(clusterApprovers);

		const keywordsRef = requirementDetails.keywords;
		const keywordsApprovers = keywordsRef?.flatMap(e => {
			const matchedKeywords = keywords?.find(f => [e.id].includes(f.id));
			return matchedKeywords?.approvers ?? [];
		});
		if (keywordsApprovers?.length >= 1)
			return deDuplicateUsers(keywordsApprovers);
		return [];
	}, [
		vexClusters,
		requirementDetails.vexClusters,
		keywords,
		requirementDetails.keywords,
	]);

	// TODO: Refactor this into property after release
	const updateRequirementDocuments = (
		requirementStatus: RequirementStatus,
		changeType: RequirementChangeType,
		extraWorkflowProps = {},
		approvers?: any,
		version?: string,
		skipModificationDataUpdate?: boolean,
	) => {
		const getInput = (
			info: ListOrDetailsRequirement | any,
		): UpdateRequirementInput => {
			const input: Input = mapToInput(info);
			return {
				...input,
				name: requirementDetails.name,
				id: requirementDetails.id,
				requirementId: requirementDetails.requirementId,
				status: requirementStatus,
				version,
				vexClusterRefs: mapToRef(requirementDetails.vexClusters),
				approvalWorkFlow: {
					approverRefs: mapToRef(approvers),
					...extraWorkflowProps,
				},
				changeType,
				skipModificationDataUpdate,
			};
		};

		return updateRequirementDocument({
			variables: {
				input: getInput(requirementDetails),
			},
			refetchQueries: [GetAllRequirementsDocument],
		});
	};

	const getApprovedVersion = (): string | undefined => {
		if (requirementDetails.version) {
			switch (requirementDetails.changeType) {
				case RequirementChangeType.Content:
					return incrementMajorVersion(requirementDetails.version);
				case RequirementChangeType.Editorial:
					return incrementMinorVersion(requirementDetails.version);
				default:
					return requirementDetails.version || getInitialVersion();
			}
		}

		return getInitialVersion();
	};

	const onSendForApprovalConfirmClick = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.Approval,
			dialogChangeType,
			{
				message: reason,
			},
			mapToRef(
				prePopulatedApprovers?.length > 0 ? prePopulatedApprovers : users,
			),
			requirementDetails.version || undefined,
			true,
		)
			.then(() => {
				setMessage(t('ApprovalSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [
		requirementDetails,
		dialogChangeType,
		users,
		prePopulatedApprovers,
		reason,
	]);

	const onConfirmWithoutApproval = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.Final,
			RequirementChangeType.None,
			{},
			null,
			incrementMinorVersion(requirementDetails?.version || '') ||
				getInitialVersion(),
			true,
		)
			.then(() => {
				setMessage(t('ApprovalOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [requirementDetails, prePopulatedApprovers]);

	const onConfirmApprovalclick = React.useCallback(async () => {
		await updateRequirementDocuments(
			RequirementStatus.Final,
			RequirementChangeType.None,
			{},
			null,
			getApprovedVersion(),
			true,
		)
			.then(() => {
				setMessage(t('ApprovalOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [requirementDetails]);

	const onDeclineclick = React.useCallback(async () => {
		await updateRequirementDocuments(
			getDeclinedRequirementStatus(requirementDetails),
			RequirementChangeType.None,
			{
				message: reason,
			},
			null,
			requirementDetails.version || undefined,
			true,
		)
			.then(() => {
				setMessage(t('ApprovalNOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [requirementDetails, reason]);

	const getChangeTypeMode = (): SendApprovalDialogChangeTypeMode => {
		if (requirementDetails?.status === RequirementStatus.Modified) {
			return SendApprovalDialogChangeTypeMode.Show;
		}
		// eslint-disable-next-line no-warning-comments
		// TODO: Disable Combobox if next major version exists

		return SendApprovalDialogChangeTypeMode.Hide;
	};

	return (
		<>
			{dialogType === RequirementStatusType.SendToApproval && (
				<SendApprovalDialog
					requirementName={requirementDetails.name}
					defaultApprovers={prePopulatedApprovers}
					{...dialogState}
					onConfirm={onSendForApprovalConfirmClick}
					users={users}
					setUsers={setUsers}
					changeType={dialogChangeType}
					setChangeType={setDialogChangeType}
					changeTypeMode={getChangeTypeMode()}
					reason={reason}
					setReason={setReason}
				/>
			)}
			{dialogType === RequirementStatusType.CompleteWithoutApproval && (
				<ConfirmWithoutApproval
					requirementName={requirementDetails.name}
					setChangeType={setDialogChangeType}
					{...dialogState}
					onConfirm={onConfirmWithoutApproval}
				/>
			)}

			{dialogType === RequirementStatusType.ClearingOK && (
				<ApprovalDialog
					requirementName={requirementDetails.name}
					{...dialogState}
					onConfirm={onConfirmApprovalclick}
					users={users}
				/>
			)}
			{dialogType === RequirementStatusType.ClearingNOK && (
				<DeclineDialog
					requirementName={requirementDetails.name}
					{...dialogState}
					onConfirm={onDeclineclick}
					reason={reason}
					setReason={setReason}
				/>
			)}
		</>
	);
};
